/**
 * Contratos compartidos con los WhatsApp Flows y con la capa (garment.models / garment.review).
 * Espejo de lo que fija tests/test_flows_json.py y tests/test_review.py: si cambia un campo aqui,
 * cambia en flows/*.json y en la capa.
 */

/** Flow 1 (UPLOAD): lo que manda el footer `complete`. En la SPA las fotos van primero a S3 (presigned PUT). */
export interface UploadPayload {
  price: string;
  size: string;
  notes: string;
  photos: { s3_key: string; file_name: string }[];
  simulate?: SimulateDirectives;
  /** Solo SPA: categoria elegida al subir ("" = la decide el clasificador). */
  category?: Category | "";
}

/** garment.models.ProductCategory / garment.categories: mismo orden y valores (tests: contracts.spec.ts). */
export type Category = "ropa" | "calzado" | "carteras" | "joyeria" | "accesorios" | "otro";
export const CATEGORIES: Category[] = ["ropa", "calzado", "carteras", "joyeria", "accesorios", "otro"];
export const CATEGORY_LABELS: Record<Category, string> = {
  ropa: "Ropa",
  calzado: "Calzado",
  carteras: "Bolsos y carteras",
  joyeria: "Joyería",
  accesorios: "Accesorios",
  otro: "Otro",
};
/** garment.categories.SIZE_REQUIRED: talla obligatoria para publicar. Sin categoria (productos viejos) = ropa. */
export const SIZE_REQUIRED: Record<Category, boolean> = {
  ropa: true,
  calzado: true,
  carteras: false,
  joyeria: false,
  accesorios: false,
  otro: false,
};
export const SIZE_LABELS: Partial<Record<Category, string>> = { calzado: "Número" };

export function isCategory(value: unknown): value is Category {
  return typeof value === "string" && (CATEGORIES as string[]).includes(value);
}

/** Campos obligatorios para aprobar segun la categoria (missing_required_for_publish). */
export function requiredForPublish(category: Category | "" | null | undefined): ReviewField[] {
  const sizeRequired = category ? SIZE_REQUIRED[category] : true;
  return sizeRequired ? ["price", "size", "garment_type"] : ["price", "garment_type"];
}

export const MAX_PHOTOS = 10;
export const MAX_PHOTO_BYTES = 10 * 1024 * 1024; // PhotoPicker: 10240 KB

/** Tipos que acepta POST /products/uploads (products_api.ALLOWED_CONTENT_TYPES). HEIC/HEIF se
 *  convierten a JPEG en ingest_media. */
export const PHOTO_TYPES_BY_EXT: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
};
/** `accept` del input: HEIC solo por extension, para que Safari en iOS siga convirtiendo a JPEG al elegir de la galeria. */
export const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,.heic,.heif";

/** Content type de una foto: el que reporta el navegador o, si viene vacio (Chrome/Windows con .heic), por extension. */
export function photoContentType(file: { name: string; type: string }): string {
  if (file.type) return file.type;
  const ext = file.name.includes(".") ? (file.name.split(".").pop() ?? "").toLowerCase() : "";
  return PHOTO_TYPES_BY_EXT[ext] ?? "image/jpeg";
}

export function isHeic(file: { name: string; type: string }): boolean {
  return ["image/heic", "image/heif"].includes(photoContentType(file));
}

/** Solo la honran los proveedores fake (tenant e2e). */
export interface SimulateDirectives {
  batch_failed?: boolean;
  fail_destinations?: Destination[];
  /** Destinos que fallan al borrar lo publicado (quedan como publicaciones pendientes de borrar a mano). */
  fail_unpublish_destinations?: Destination[];
  delay_seconds?: number;
  drop_key?: string | null;
  extract_size?: string | null;
  classify_category?: Category | null;
  classify_uncertain?: boolean;
}

export type Destination = "woocommerce" | "instagram" | "facebook";
export const DESTINATIONS: Destination[] = ["woocommerce", "instagram", "facebook"];
export const DESTINATION_LABELS: Record<Destination, string> = {
  woocommerce: "WooCommerce",
  instagram: "Instagram",
  facebook: "Facebook",
};
export type Decision = "approve" | "regenerate";

/** Flow 2 (REVIEW): los 11 campos del `data_exchange` mas `category` (solo SPA). Un campo vacio = sin cambio. */
export interface ReviewPayload {
  category: Category | "";
  decision: Decision;
  price: string;
  size: string;
  garment_type: string;
  brand: string;
  fabric: string;
  color: string;
  extra_details: string;
  destinations: Destination[];
  regenerate_image_id: string;
  regenerate_note: string;
}

/** Campos editables de la revision (los 7 de detalle del ReviewPayload mas la categoria). */
export type ReviewField =
  "category" | "garment_type" | "brand" | "fabric" | "color" | "price" | "size" | "extra_details";

/** Lo que exige garment.models.ProductDetails.missing_required_for_publish para ropa (`missing` usa estos
 *  nombres). Con categoria, usar `requiredForPublish(category)`. */
export const REQUIRED_FOR_PUBLISH: ReviewField[] = ["price", "size", "garment_type"];

/** Datos de la pantalla REVIEW (garment.review.build_review_screen). En la SPA `src` es una URL prefirmada. */
export interface ReviewScreen {
  title: string;
  garment_type: string;
  brand: string;
  size: string;
  price: string;
  fabric: string;
  color: string;
  extra_details: string;
  images: { id: string; src: string; "alt-text": string }[];
  image_options: { id: string; title: string }[];
  destinations: { id: Destination; title: string }[];
  selected_destinations: Destination[];
  missing: string[];
  /** Solo SPA (products_api): destinos sin cuenta conectada; se muestran deshabilitados. */
  unavailable_destinations?: Destination[];
  /** Solo SPA (garment.review.category_fields): "" en productos anteriores a las categorias. */
  category?: Category | "";
  category_label?: string;
  category_uncertain?: boolean;
  category_options?: { id: Category; title: string }[];
  size_required?: boolean;
  size_label?: string;
}

export type ProductStatus =
  | "SUBMITTED"
  | "INGESTING"
  | "EXTRACTED"
  | "GENERATING"
  | "REVIEW_PENDING"
  | "REGENERATING"
  | "PUBLISHING"
  | "PUBLISHED"
  | "PARTIALLY_PUBLISHED"
  | "FAILED"
  | "DELETING";

/** `mannequin` es el nombre anterior de `producto` (el backend ya lo devuelve como `producto`). */
export type ImageProfile = "lifestyle" | "producto" | "mannequin";

export interface GeneratedImage {
  image_id: string;
  profile: ImageProfile;
  version: number;
  s3_key_preview: string | null;
  public_url: string | null;
  error: string | null;
}

export interface ProductDetails {
  garment_type: string;
  size: string;
  price: number | null;
  fabric: string;
  color: string;
  brand: string;
  extra_details: string;
}

/** garment.models.ProductCopy */
export interface ProductCopy {
  title: string;
  short_description: string;
  long_description: string;
  meta_description: string;
  instagram_description: string;
  instagram_tags: string[];
  woocommerce_tags: string[];
}

export interface ProductRecord {
  tenant_id: string;
  product_id: string;
  status: ProductStatus;
  channel: "whatsapp" | "web";
  submitted_price: number | null;
  submitted_notes: string;
  details: ProductDetails | null;
  copy: Partial<ProductCopy> | null;
  images: GeneratedImage[];
  regeneration_count: number;
  last_step: string | null;
  simulate: SimulateDirectives | null;
  created_at: string;
  updated_at: string;
  category?: Category | null;
  category_source?: "seller" | "classifier" | "fallback" | "";
  category_uncertain?: boolean;
  classification?: ClassificationResult | null;
  prompt_refs?: Record<string, string>;
}

/** garment.models.ClassificationResult */
export interface ClassificationResult {
  category: Category;
  confidence: number;
  probabilities: Record<string, number>;
  uncertain: boolean;
  source: "classifier" | "seller" | "fallback";
  model: string;
  usage?: Record<string, number>;
  cost_usd?: number | null;
  refusal?: string | null;
  error?: string | null;
}

export interface Notification {
  kind: "review_ready" | "reminder" | "result" | "failed";
  body: string;
  buttons: { id: string; title: string }[];
  created_at: string;
}

/** garment.models.PublishResult (ultimo resultado de cada destino). */
export interface PublishResult {
  destination: Destination;
  status: "published" | "skipped" | "failed";
  external_id: string | null;
  url: string | null;
  error: string | null;
  /** Ids remotos para borrar lo publicado (Woo {product_id, media_ids}, IG {media_id}, FB {post_id, photo_ids}). */
  remote_refs?: Record<string, string | string[]>;
}

export interface ProductResponse {
  product: ProductRecord;
  review: ReviewScreen | null;
  notifications: Notification[];
  publish_results: PublishResult[];
  /** Fotos originales (URL prefirmadas), en el orden en que se subieron. */
  original_urls: string[];
  /** {image_id: public_url o preview prefirmada} de cada imagen generada. */
  image_urls: Record<string, string>;
  max_regenerations: number;
}

export const DEFAULT_MAX_REGENERATIONS = 3;

export const TERMINAL: ProductStatus[] = ["PUBLISHED", "PARTIALLY_PUBLISHED", "FAILED"];
export const RETRYABLE: ProductStatus[] = ["FAILED", "PARTIALLY_PUBLISHED"];
/** Estados en los que DELETE /products/{id} se permite (garment.cleanup.DELETABLE): fuera del pipeline. */
export const DELETABLE: ProductStatus[] = ["REVIEW_PENDING", "PUBLISHED", "PARTIALLY_PUBLISHED", "FAILED", "DELETING"];
/** Antes de la revision: todavia no hay imagenes generadas (el carrusel muestra las originales). */
export const EARLY: ProductStatus[] = ["SUBMITTED", "INGESTING", "EXTRACTED", "GENERATING"];

/** Item de GET /products: el ProductRecord mas la miniatura que calcula products_api
 *  (public_url de la ultima imagen publicada, o su preview prefirmada, o null). */
export interface ProductListItem extends ProductRecord {
  thumbnail_url: string | null;
}

/** Grupos de estado de la biblioteca (filtros de la pagina `/`). */
export type StatusGroup = "all" | "processing" | "review" | "published" | "error";
export const STATUS_GROUP_ORDER: StatusGroup[] = ["all", "processing", "review", "published", "error"];
export const STATUS_GROUPS: Record<Exclude<StatusGroup, "all">, ProductStatus[]> = {
  processing: ["SUBMITTED", "INGESTING", "EXTRACTED", "GENERATING", "REGENERATING", "PUBLISHING", "DELETING"],
  review: ["REVIEW_PENDING"],
  published: ["PUBLISHED", "PARTIALLY_PUBLISHED"],
  error: ["FAILED"],
};
export const STATUS_GROUP_LABELS: Record<StatusGroup, string> = {
  all: "Todos",
  processing: "En curso",
  review: "Por revisar",
  published: "Publicados",
  error: "Con fallos",
};

export function statusGroup(status: ProductStatus): Exclude<StatusGroup, "all"> {
  for (const [group, statuses] of Object.entries(STATUS_GROUPS)) {
    if (statuses.includes(status)) return group as Exclude<StatusGroup, "all">;
  }
  return "processing";
}

/** Etiqueta legible de cada estado (el valor crudo sigue en data-status). */
export const STATUS_LABELS: Record<ProductStatus, string> = {
  SUBMITTED: "Recibido",
  INGESTING: "Preparando fotos",
  EXTRACTED: "Leyendo detalles",
  GENERATING: "Generando imágenes",
  REVIEW_PENDING: "Por revisar",
  REGENERATING: "Regenerando",
  PUBLISHING: "Publicando",
  PUBLISHED: "Publicado",
  PARTIALLY_PUBLISHED: "Publicado en parte",
  FAILED: "Falló",
  DELETING: "Eliminando",
};

/** Clase de la etiqueta de estado (diseño: coral = por revisar, teal = fallos, contorno = en curso). */
export const STATUS_TAG: Partial<Record<ProductStatus, string>> = {
  REVIEW_PENDING: "tag-accent",
  PUBLISHED: "tag-neutral",
  PARTIALLY_PUBLISHED: "tag-accent-2",
  FAILED: "tag-accent-2",
};
export function statusTagClass(status: ProductStatus): string {
  return STATUS_TAG[status] ?? "tag-outline";
}

/** Nombre legible del paso de la ASL donde fallo el producto (`last_step`). */
export const STEP_LABELS: Record<string, string> = {
  IngestMedia: "Preparar fotos",
  ExtractDetails: "Leer detalles",
  CreateImageBatch: "Generar imágenes",
  ProcessBatchResults: "Generar imágenes",
  SendReviewFlow: "Enviar a revisión",
  ApplyEdits: "Aplicar tus cambios",
  PublishWoo: "Publicar en WooCommerce",
  PublishInstagram: "Publicar en Instagram",
  PublishFacebook: "Publicar en Facebook",
  NotifyResult: "Avisar el resultado",
  Review: "Revisión",
  Retry: "Reintento",
};

export const PROFILE_LABELS: Record<ImageProfile, string> = {
  producto: "Producto",
  mannequin: "Producto",
  lifestyle: "Lifestyle",
};

/** garment.models.MetaConnection: estado visible de la conexion con Facebook/Instagram (el token no sale del backend). */
export type MetaConnectionStatus = "connected" | "needs_reauth" | "pending_selection" | "disconnected";

export interface MetaPageCandidate {
  page_id: string;
  page_name: string;
  ig_account_id: string | null;
  ig_username: string | null;
}

export interface MetaConnection {
  tenant_id: string;
  status: MetaConnectionStatus;
  token_type: "system_user" | "manual";
  page_id: string | null;
  page_name: string;
  ig_account_id: string | null;
  ig_username: string | null;
  scopes: string[];
  /** Epoch en segundos; 0 = no vence. */
  expires_at: number;
  data_access_expires_at: number;
  candidates: MetaPageCandidate[];
  connected_at: string | null;
  /** Ultima renovacion automatica del token de 60 dias. */
  refreshed_at?: string | null;
  last_checked_at: string | null;
  last_error: string | null;
}

/** garment.models.OrphanPost: publicación que no se pudo borrar al borrar el producto. */
export interface OrphanPost {
  tenant_id: string;
  product_id: string;
  destination: Destination;
  product_title: string;
  url: string | null;
  external_id: string | null;
  error: string;
  created_at: string;
}

/** garment.models.WhatsAppConnection: número de WhatsApp Business conectado con Embedded Signup (Coexistence). */
export type WhatsAppConnectionStatus = MetaConnectionStatus;

export interface WhatsAppPhoneCandidate {
  phone_number_id: string;
  waba_id: string;
  display_phone_number: string;
  verified_name: string;
  platform_type: string | null;
  is_on_biz_app: boolean | null;
}

export interface WhatsAppConnection {
  tenant_id: string;
  status: WhatsAppConnectionStatus;
  waba_id: string | null;
  phone_number_id: string | null;
  display_phone_number: string;
  verified_name: string;
  quality_rating: string | null;
  platform_type: string | null;
  /** true: el número sigue funcionando en la app WhatsApp Business (coexistencia). */
  is_on_biz_app: boolean | null;
  scopes: string[];
  /** Epoch en segundos; 0 = no vence. */
  expires_at: number;
  candidates: WhatsAppPhoneCandidate[];
  connected_at: string | null;
  last_checked_at: string | null;
  last_error: string | null;
  last_event: string | null;
  last_event_at: string | null;
}

/** garment.whatsapp_connect.signup_config: lo que el SDK de Facebook necesita para abrir Embedded Signup. */
export interface WhatsAppSignupConfig {
  app_id: string;
  config_id: string;
  api_version: string;
  feature_type: string;
}

/** POST /connections/whatsapp */
export interface WhatsAppConnectPayload {
  code: string;
  waba_id?: string;
  phone_number_id?: string;
}

/** GET /connections */
export interface ConnectionsResponse {
  meta: MetaConnection | null;
  /** false si la app de Meta todavía no está configurada en el backend (no se ofrece "Conectar"). */
  meta_login_available: boolean;
  destinations: Partial<Record<Destination, boolean>>;
  orphans: OrphanPost[];
  whatsapp: WhatsAppConnection | null;
  /** null si Embedded Signup no está configurado (no se ofrece "Conectar WhatsApp"). */
  whatsapp_signup: WhatsAppSignupConfig | null;
}

/** Resultado del callback de OAuth (`/settings?meta=...`). */
export type MetaLoginOutcome = "connected" | "select" | "error";

// ----------------------------------------------------------------- prompts por categoria ---
/** garment.prompts.PROMPT_KEYS */
export type PromptKey = "extract_system" | "lifestyle_builder" | "copy_system" | "producto";
export const PROMPT_KEYS: PromptKey[] = ["extract_system", "lifestyle_builder", "copy_system", "producto"];
export const PROMPT_KEY_LABELS: Record<PromptKey, string> = {
  extract_system: "Extracción",
  lifestyle_builder: "Guía lifestyle",
  copy_system: "Copy",
  producto: "Imagen de producto",
};
export const PROMPT_KEY_HINTS: Record<PromptKey, string> = {
  extract_system:
    "Instrucciones de la extracción: detalles, copy y el prompt lifestyle en una sola llamada. {{LIFESTYLE_BUILDER}} marca dónde va la guía lifestyle.",
  lifestyle_builder: "Cómo construir el prompt de la imagen lifestyle (locaciones, estilo, formato, tipografía).",
  copy_system: "Instrucciones para reescribir el copy cuando la clienta corrige datos en la revisión.",
  producto: "Prompt fijo de la segunda imagen: foto de producto en estudio.",
};
export const PROMPT_MAX_CHARS = 20_000;
export type PromptScope = "global" | "tenant";
export type PromptTextScope = "bundled" | "global" | "tenant" | "draft";

/** garment.prompts.PromptText. `version` 0 = archivo del repo. */
export interface PromptText {
  key: PromptKey;
  category: Category;
  scope: PromptTextScope;
  tenant_id: string;
  version: number;
  text?: string;
  note: string;
  updated_at: string;
  updated_by: string;
  restored_from: number | null;
}

/** GET /prompts */
export interface PromptsIndexResponse {
  categories: {
    category: Category;
    label: string;
    prompts: Record<PromptKey, { effective: PromptText; global: PromptText | null; tenant: PromptText | null }>;
  }[];
  keys: PromptKey[];
}

/** GET /prompts/{scope}/{category}/{key} */
export interface PromptDetailResponse {
  scope: PromptScope;
  category: Category;
  key: PromptKey;
  current: PromptText | null;
  /** Lo que rige sin version vigente en este scope (global o archivo); sirve para el diff. */
  base: PromptText;
  bundled: PromptText;
  history: PromptText[];
}

// ------------------------------------------------------------------------ laboratorio ---
export type LabStatus = "queued" | "running" | "done" | "failed";
export const LAB_ACTIVE: LabStatus[] = ["queued", "running"];
export const LAB_STATUS_LABELS: Record<LabStatus, string> = {
  queued: "En cola",
  running: "Ejecutando",
  done: "Terminada",
  failed: "Falló",
};
export type LabOutputKind = "classification" | "extraction" | "copy" | "lifestyle" | "producto";
export const LAB_OUTPUT_KINDS: LabOutputKind[] = ["classification", "extraction", "copy", "lifestyle", "producto"];
export const LAB_OUTPUT_LABELS: Record<LabOutputKind, string> = {
  classification: "Clasificación",
  extraction: "Extracción",
  copy: "Copy",
  lifestyle: "Imagen lifestyle",
  producto: "Imagen de producto",
};

export interface LabRating {
  score: number;
  note: string;
  rated_at: string;
  rated_by: string;
}

export interface JudgeScore {
  score: number;
  confidence: number;
  level: string;
  model: string;
  cost_usd?: number | null;
}

export interface LabOutput {
  kind: LabOutputKind;
  status: "pending" | "running" | "ok" | "error" | "skipped";
  error: string | null;
  model: string;
  seconds: number;
  usage: Record<string, number>;
  cost_usd: number | null;
  prompt: string;
  prompt_ref: string;
  payload: Record<string, unknown>;
  s3_key_original: string | null;
  s3_key_preview: string | null;
  judge: JudgeScore | null;
  judge_error: string | null;
  rating: LabRating | null;
}

export interface LabTest {
  test_id: string;
  tenant_id: string;
  created_by: string;
  label: string;
  status: LabStatus;
  error: string | null;
  photos: string[];
  original_keys: string[];
  notes: string;
  price: number | null;
  forced_category: Category | null;
  category: Category | null;
  category_source: string;
  prompt_source: PromptScope;
  draft_prompts: Partial<Record<PromptKey, string>>;
  prompt_refs: Partial<Record<PromptKey, string>>;
  outputs: LabOutput[];
  total_cost_usd: number;
  total_seconds: number;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  updated_at: string;
}

/** Fila de GET /lab/tests (garment.lab.LabTest.summary). */
export interface LabTestSummary extends Omit<LabTest, "outputs" | "draft_prompts"> {
  draft_keys: PromptKey[];
  is_draft: boolean;
  avg_rating: number | null;
  avg_judge: number | null;
  rated: number;
}

export interface LabTestResponse {
  test: LabTest;
  original_urls: string[];
  image_urls: Partial<Record<LabOutputKind, string>>;
  download_urls: Partial<Record<LabOutputKind, string>>;
}

/** Fila de GET /lab/summary (garment.lab.comparison_rows). */
export interface LabSummaryRow {
  category: Category;
  prompt_source: PromptScope;
  is_draft: boolean;
  prompt_refs: Partial<Record<PromptKey, string>>;
  tests: number;
  rated_tests: number;
  avg_rating: number | null;
  avg_judge: number | null;
  avg_cost_usd: number;
  avg_seconds: number;
  last_at: string;
}

/** POST /lab/tests */
export interface LabTestCreate {
  photos: { s3_key: string; file_name: string }[];
  category: Category | "";
  prompt_source: PromptScope;
  draft_prompts: Partial<Record<PromptKey, string>>;
  price: string;
  size: string;
  notes: string;
  label: string;
}
